import Capsule from "layout/Capsule";
import Header from "layout/header/Header";

import DayNavigator from "components/DayNavigator";

import useDaySelector from "hooks/useDaySelector";
import { useEffect, useState } from "react";
import axios from "axios";
import { IoChevronBackOutline, IoChevronForwardOutline, IoInformationCircleOutline } from 'react-icons/io5';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';

import defaultAvatar from 'assets/img/avatar_default.jpg';
import PlaceHolderItemList from "components/loader/PlaceHolderItemList";
import { formatMinutes, capitalizeFirstLetter } from "utils/helpers";
import EditorSpecModal from "pages/stats/components/EditorSpecModal";

const CATEGORY_LABELS = {
    FOTO: 'Foto',
    VIDEO: 'Video',
    RENDER: 'Render',
    PLAN: 'Planimetrie',
};

// Intervallo (mese o settimana) che contiene il giorno di riferimento,
// con etichetta leggibile per il navigatore del tab "Tempo consegna".
function periodRange(day, period) {
    if (period === 'week') {
        const start = day.clone().startOf('isoWeek');
        const end = day.clone().endOf('isoWeek');
        return {
            from: start.format('YYYY-MM-DD'),
            to: end.format('YYYY-MM-DD'),
            label: `Settimana ${start.format('D MMM')} - ${end.format('D MMM YYYY')}`,
        };
    }
    const start = day.clone().startOf('month');
    const end = day.clone().endOf('month');
    return {
        from: start.format('YYYY-MM-DD'),
        to: end.format('YYYY-MM-DD'),
        label: `Mese di ${start.format('MMMM YYYY')}`,
    };
}

function useStatsEditor() {

    const [tab, setTab] = useState('edited');
    const [stats, setStats] = useState();
    const [delivery, setDelivery] = useState();
    const [period, setPeriod] = useState('month'); // 'month' | 'week'

    // Modale dettaglio editor ("Statistiche utente"): editor selezionato + dati per servizio.
    const [specEditor, setSpecEditor] = useState(null); // { id, name, avatar }
    // Range su cui la modale interroga i servizi: giorno (tab "Servizi editati")
    // o periodo mese/settimana (tab "Tempo consegna"). { from, to, label }
    const [specRange, setSpecRange] = useState(null);
    const [spec, setSpec] = useState();

    const { next, previous, day, setDay, formatDate } = useDaySelector();

    const range = periodRange(day, period);

    // specRangeOverride: se assente usa il periodo corrente (tab "Tempo consegna").
    const openSpec = (editor, specRangeOverride) => {
        setSpec();
        setSpecRange(specRangeOverride || { from: range.from, to: range.to, label: range.label });
        setSpecEditor({ id: editor.id, name: editor.name, avatar: editor.avatar });
    };
    const closeSpec = () => { setSpecEditor(null); setSpec(); setSpecRange(null); };

    // Dettaglio per singolo editor sul range con cui e' stata aperta la modale.
    useEffect(() => {
        if (!specEditor || !specRange) return;
        setSpec();
        axios.get('stats/editor-spec', { params: { from: specRange.from, to: specRange.to, editor_id: specEditor.id } })
            .then(res => setSpec(res.data));
    }, [specEditor, specRange]);

    // Tab "Servizi editati": dati per giorno
    useEffect(() => {
        if (tab !== 'edited') return;
        setStats();
        axios.get('stats/editor', { params: { day: formatDate.start } })
            .then(res => setStats(res.data));
    }, [day, tab]);

    // Tab "Tempo consegna": dati per periodo (mese o settimana del giorno di riferimento)
    useEffect(() => {
        if (tab !== 'delivery') return;
        setDelivery();
        axios.get('stats/editor-delivery', { params: { from: range.from, to: range.to } })
            .then(res => setDelivery(res.data));
    }, [range.from, range.to, tab]);

    // Le frecce si muovono di un periodo intero (settimana/mese), non di un giorno.
    const stepPeriod = (dir) => setDay(day.clone().add(dir, period === 'week' ? 'weeks' : 'months'));

    return { tab, setTab, next, previous, day, stats, delivery, period, setPeriod, range, stepPeriod, specEditor, specRange, spec, openSpec, closeSpec };
}

// Larghezze fisse delle colonne (riga + intestazione restano allineate).
// Editor a larghezza fissa cosi' la barra Avanzamento resta uguale per tutti.
const COL_EDITOR = 200;
const COL_PROGRESS = 460;
const COL_COMPLETED = 64;
const COL_AVG = 80;
// Tab "Tempo consegna": colonne Tempo medio + Trend
const COL_DAVG = 96;
const COL_DTREND = 84;

function EditedHeader() {
    return (
        <li className="text-12 text-gray fw-500 px-2 py-1">
            <div className="d-flex align-items-center">
                <span style={{ flex: '0 0 44px' }} />
                <span style={{ flex: `0 0 ${COL_EDITOR}px`, minWidth: 0 }}>Editor</span>
                <span style={{ flex: '1 1 auto' }} />
                <span className="text-center me-3" style={{ flex: `0 0 ${COL_PROGRESS}px` }}>Avanzamento</span>
                <span className="text-center me-3" style={{ flex: `0 0 ${COL_COMPLETED}px` }}>Completati</span>
                <span className="text-center d-inline-flex align-items-center justify-content-center" style={{ flex: `0 0 ${COL_AVG}px` }}>
                    {/* <OverlayTrigger
                        placement="top"
                        trigger={['hover', 'focus', 'click']}
                        overlay={
                            <Tooltip id="tempo-medio-tooltip">
                                Tempo medio tra l'assegnazione del lavoro all'editor e la consegna, pesato sul numero di contenuti (i "Completati"). È tempo di calendario: include l'attesa in coda, le notti e i weekend, non le ore di lavoro effettive.
                            </Tooltip>
                        }
                    >
                        <span role="button" tabIndex={0} className="me-1 d-inline-flex" style={{ cursor: 'pointer' }}>
                            <IoInformationCircleOutline />
                        </span>
                    </OverlayTrigger> */}
                    Tempo medio
                </span>
            </div>
        </li>
    );
}

function EditorLine({ stat, isSkeleton, onSelect }) {

    const percentage = isSkeleton ? 0 : stat.completed * 100 / (stat.content_x_day ?? 1);
    const avgMinutes = isSkeleton ? null : (
        stat.avg_assignment_to_delivery_hours !== null && stat.avg_assignment_to_delivery_hours !== undefined
            ? stat.avg_assignment_to_delivery_hours * 60
            : null
    );

    // Al click apre la modale dettaglio editor (stessa logica del tab "Tempo consegna").
    const clickable = !isSkeleton && onSelect;
    const handleClick = clickable
        ? () => onSelect({ id: stat.editor, name: stat.display_name, avatar: stat.avatar_url })
        : undefined;

    return (
        <li>
            <div
                className="item align-items-center pe-2"
                {...(clickable ? { role: 'button', style: { cursor: 'pointer' }, onClick: handleClick } : {})}
            >
                <div className="avatar me-2">
                    <img src={stat?.avatar_url || defaultAvatar} alt="avatar" className="imaged rounded w36" />
                </div>
                {isSkeleton ? <PlaceHolderItemList hideSecondLine /> :
                    <div className="in">
                        <div style={{ flex: `0 0 ${COL_EDITOR}px`, minWidth: 0, overflow: 'hidden' }}>
                            {stat.display_name}
                            <br />
                            <span className="text-12 text-gray">
                                {stat.editor_roles.join(', ')}
                            </span>
                        </div>
                        <div style={{ flex: '1 1 auto' }} />
                        <div className="tooltip-css me-3" style={{ flex: `0 0 ${COL_PROGRESS}px`, minWidth: 0 }}>
                            <span className="tooltiptext p-1 ms-3">
                                {stat.grouped.length > 0 ? (
                                    stat.grouped.map(([type, total], index) => (
                                        <div key={index}>
                                            <b>{type}</b>: {total}
                                        </div>
                                    ))
                                ) : 'Nessun servizio editato'}
                            </span>
                            <div className={`progress position-relative w-100 ${isSkeleton ? 'w-100 placeholder' : ''}`}>
                                <div
                                    className={`progress-bar bg-${percentage >= 100 ? 'success' : (percentage < 50 ? 'danger' : 'warning')}`}
                                    role="progressbar"
                                    style={{ width: isSkeleton ? 0 : (percentage > 100 ? 100 : percentage) + '%' }}
                                    aria-valuenow={isSkeleton ? 0 : Math.round(percentage)}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ><span className="progress-text">{stat.completed}/{stat.content_x_day}</span></div>
                            </div>
                        </div>
                        <div className="text-center fw-500 me-3" style={{ flex: `0 0 ${COL_COMPLETED}px` }}>
                            {stat.completed}
                        </div>
                        <div className="text-center text-12" style={{ flex: `0 0 ${COL_AVG}px` }}>
                            {formatMinutes(avgMinutes)}
                        </div>
                    </div>
                }
            </div>
        </li>
    )
}

// Freccia trend: trend in minuti. Negativo = piu' veloce (verde), positivo = piu' lento (rosso).
function TrendArrow({ minutes }) {
    const faster = minutes < 0;
    if (minutes === null || minutes === undefined || minutes === 0) {
        return <span className="text-12 text-gray">=</span>;
    }
    return (
        <span className={`text-12 fw-500 text-${faster ? 'success' : 'danger'}`}>
            {faster ? '↓' : '↑'} {formatMinutes(Math.abs(minutes))}
        </span>
    );
}

function DeliveryHeader() {
    return (
        <li className="text-12 text-gray fw-500 px-2 py-1">
            <div className="d-flex align-items-center">
                <span style={{ flex: '0 0 44px' }} />
                <span style={{ flex: `0 0 ${COL_EDITOR}px`, minWidth: 0 }}>Editor</span>
                <span className="text-center me-3" style={{ flex: '1 1 auto' }}>Avanzamento</span>
                <span className="text-center me-3" style={{ flex: `0 0 ${COL_DAVG}px` }}>Tempo medio</span>
                <span className="text-center" style={{ flex: `0 0 ${COL_DTREND}px` }}>Trend</span>
            </div>
        </li>
    );
}

function DeliveryEditorLine({ editor, onSelect }) {
    const avg = editor.average_time_minutes;

    let percentage = 0;
    if (editor.target && editor.target > 0) {
        percentage = editor.completed_count * 100 / editor.target;
    }

    return (
        <li>
            <div
                className="item align-items-center pe-2"
                role="button"
                style={{ cursor: 'pointer' }}
                onClick={() => onSelect(editor)}
            >
                <div className="avatar me-2">
                    <img src={editor.avatar || defaultAvatar} alt="avatar" className="imaged rounded w36" />
                </div>
                <div className="in">
                    <div style={{ flex: `0 0 ${COL_EDITOR}px`, minWidth: 0, overflow: 'hidden' }}>
                        {editor.name}
                    </div>
                    <div className="tooltip-css me-3" style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <span className="tooltiptext p-1 ms-3">
                            <b>{editor.category === 'FOTO' ? 'PHOTO' : editor.category}</b>: {editor.completed_count}
                        </span>
                        <div className="progress position-relative w-100">
                            <div
                                className={`progress-bar bg-${percentage >= 100 ? 'success' : (percentage < 50 ? 'danger' : 'warning')}`}
                                role="progressbar"
                                style={{ width: (percentage > 100 ? 100 : percentage) + '%' }}
                                aria-valuenow={Math.round(percentage)}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            >
                                <span className="progress-text">{editor.target_progress || editor.completed_count}</span>
                            </div>
                        </div>
                    </div>
                    <div className="text-center me-3" style={{ flex: `0 0 ${COL_DAVG}px` }}>
                        {formatMinutes(avg)}
                    </div>
                    <div className="text-center" style={{ flex: `0 0 ${COL_DTREND}px` }}>
                        <TrendArrow minutes={editor.trend_minutes} />
                    </div>
                </div>
            </div>
        </li>
    );
}

export default function StatsEditor() {

    const { tab, setTab, next, previous, day, stats, delivery, period, setPeriod, range, stepPeriod, specEditor, specRange, spec, openSpec, closeSpec } = useStatsEditor();

    // Nel tab "Servizi editati" la modale mostra i servizi del giorno selezionato
    // (stesso range della colonna "Completati"), non del mese.
    const openSpecForDay = (editor) => openSpec(editor, {
        from: day.format('YYYY-MM-DD'),
        to: day.format('YYYY-MM-DD'),
        label: day.format('dddd D MMMM YYYY'),
    });

    const categories = delivery?.categories || {};
    const hasDeliveryData = delivery && Object.values(categories).some(list => list.length > 0);

    return (
        <>
            <Header show_agency={false} pageTitle="Statistiche editor" />
            <Capsule>
                {/* Tab switch */}
                <div className="section mt-2">
                    <div className="btn-group w-100" role="group">
                        <button
                            className={`btn ${tab === 'edited' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setTab('edited')}
                        >Servizi editati</button>
                        <button
                            className={`btn ${tab === 'delivery' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setTab('delivery')}
                        >Tempo consegna</button>
                    </div>
                </div>

                {tab === 'edited' && (
                    <>
                        <DayNavigator next={next} previous={previous} day={day} />
                        <div className='section'>
                            <div className="card">
                                <div className="card-body">
                                    <h3 className="mb-0">Servizi editati</h3>
                                </div>
                                <ul className="listview image-listview rounded placeholder-glow">
                                    <EditedHeader />
                                    {!stats && Array(5).fill(null).map((_, index) => <EditorLine key={index} isSkeleton />)}
                                    {stats && stats.map(stat => <EditorLine key={stat.editor} stat={stat} onSelect={openSpecForDay} />)}
                                </ul>
                            </div>
                        </div>
                    </>
                )}

                {tab === 'delivery' && (
                    <div className="d-flex flex-column gap-2">
                        <div className="appHeader position-relative">
                            <div className="left">
                                <a className="headerButton" onClick={() => stepPeriod(-1)}>
                                    <IoChevronBackOutline />
                                </a>
                            </div>
                            <div className="pageTitle">{capitalizeFirstLetter(range.label)}</div>
                            <div className="right">
                                <a className="headerButton" onClick={() => stepPeriod(1)}>
                                    <IoChevronForwardOutline />
                                </a>
                            </div>
                        </div>
                        <div className="section mt-2">
                            <div className="form-group boxed custom py-0">
                                <div className="input-wrapper">
                                    <Form.Label>Periodo</Form.Label>
                                    <Form.Select
                                        className="form-control"
                                        value={period}
                                        onChange={(e) => setPeriod(e.target.value)}
                                    >
                                        <option value="month">Mensile</option>
                                        <option value="week">Settimanale</option>
                                    </Form.Select>
                                </div>
                            </div>
                        </div>
                        {/* <div className="section text-center text-12 text-gray mt-1 mb-2">
                            Soglia consegna {formatMinutes(delivery?.sla_minutes)} (badge verde se sotto, arancione se sopra)
                        </div> */}
                        {!delivery && (
                            <div className="section">
                                <div className="card"><ul className="listview image-listview rounded placeholder-glow">
                                    {Array(4).fill(null).map((_, i) => <li key={i}><div className="item"><PlaceHolderItemList /></div></li>)}
                                </ul></div>
                            </div>
                        )}
                        {delivery && !hasDeliveryData && (
                            <div className="section">
                                <div className="card"><div className="card-body text-center text-gray">
                                    Nessuna consegna nel periodo selezionato.
                                </div></div>
                            </div>
                        )}
                        {delivery && Object.keys(CATEGORY_LABELS).map(cat => (
                            categories[cat] && categories[cat].length > 0 ? (
                                <div className="section" key={cat}>
                                    <div className="card">
                                        <div className="card-body pb-1">
                                            <h3 className="mb-0">{CATEGORY_LABELS[cat]}</h3>
                                        </div>
                                        <ul className="listview image-listview rounded">
                                            <DeliveryHeader />
                                            {categories[cat].map(editor => (
                                                <DeliveryEditorLine
                                                    key={editor.id}
                                                    editor={editor}
                                                    onSelect={openSpec}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            ) : null
                        ))}
                    </div>
                )}

                <EditorSpecModal
                    show={!!specEditor}
                    onClose={closeSpec}
                    editor={specEditor}
                    periodLabel={capitalizeFirstLetter(specRange?.label || range.label)}
                    spec={spec}
                />
            </Capsule>
        </>
    );
}
